import { logEvent } from '../analytics/firAnalytics'

export type AppError = {
  message: string
  code?: string | number
  details?: unknown
}

/**
 * Handle an auth-related errors
 *
 * @param analyticsTag   Event name from ANALYTICS_TAGS
 * @param error          Unknown error thrown/caught
 * @param context        Where the error happened
 * @param extraParams    Any additional key/value pairs to include
 * @returns              Normalized error object
 */
export const handleAuthError = (
  analyticsTag: string,
  error: unknown,
  context: string,
  extraParams: Record<string, unknown> = {}
): AppError => {
  const normalized = normalizeError(error)

  logEvent(analyticsTag, {
    error: normalized,
    context,
    product: 'stats',
    ...extraParams,
  })

  console.error(`[Auth Error – ${context}]`, error)
  return normalized
}

export const normalizeError = (err: unknown): AppError => ({
  message: err instanceof Error ? err.message : String(err),
  code:
    err instanceof Error && 'code' in err ? (err.code as string | number) : undefined,
  details: err,
})

/**
 * Sanitizes error details to remove sensitive information before logging
 * Removes tokens, passwords, API keys, and other sensitive data
 *
 * @param error - The error object to sanitize
 * @returns Sanitized error object safe for logging
 */
export const sanitizeErrorForLogging = (error: unknown): Record<string, unknown> => {
  const sensitiveKeys = [
    'token',
    'password',
    'apiKey',
    'secret',
    'authorization',
    'sessionId',
  ]
  const MAX_DEPTH = 10
  const seen = new WeakSet<object>()

  const sanitize = (obj: Record<string, unknown>, depth = 0): Record<string, unknown> => {
    if (depth > MAX_DEPTH) {
      return { '[Max Depth Exceeded]': true }
    }
    if (seen.has(obj)) {
      return { '[Circular]': true }
    }
    seen.add(obj)
    const sanitized: Record<string, unknown> = {}

    for (const [key, value] of Object.entries(obj)) {
      const lowerKey = key.toLowerCase()
      const isSensitive = sensitiveKeys.some((sensitiveKey) =>
        lowerKey.includes(sensitiveKey.toLowerCase())
      )

      if (isSensitive) {
        sanitized[key] = '[REDACTED]'
      } else if (Array.isArray(value)) {
        sanitized[key] = value.map((item) =>
          item && typeof item === 'object' && !Array.isArray(item)
            ? sanitize(item as Record<string, unknown>, depth + 1)
            : item
        )
      } else if (value && typeof value === 'object') {
        // Handle plain objects, skip built-in types like Date, RegExp
        if (Object.getPrototypeOf(value) === Object.prototype) {
          sanitized[key] = sanitize(value as Record<string, unknown>, depth + 1)
        } else {
          sanitized[key] = value
        }
      } else {
        sanitized[key] = value
      }
    }

    return sanitized
  }

  if (error instanceof Error) {
    return {
      name: error.name,
      message: error.message,
      stack: error.stack,
    }
  }

  if (typeof error === 'object' && error !== null) {
    return sanitize(error as Record<string, unknown>)
  }

  return { error: String(error) }
}

/**
 * Handles general application errors with proper logging and analytics
 * Sanitizes sensitive data before logging
 *
 * @param analyticsTag - Event name from ANALYTICS_TAGS
 * @param error - Unknown error thrown/caught
 * @param context - Where the error happened
 * @param extraParams - Any additional key/value pairs to include
 * @returns Normalized error object
 */
export const handleAppError = (
  analyticsTag: string,
  error: unknown,
  context: string,
  extraParams: Record<string, unknown> = {}
): AppError => {
  const normalized = normalizeError(error)
  const sanitizedError = sanitizeErrorForLogging(error)

  logEvent(analyticsTag, {
    error: sanitizedError,
    context,
    product: 'stats',
    ...extraParams,
  })

  console.error(`[App Error – ${context}]`, sanitizedError)
  return normalized
}
